import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  Loading,
  Modal,
  PageHeader,
  Select,
  Toast,
} from '@/components/ui';
import { formatRelative } from '@/lib/format';

interface PlatformUser {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  last_login_at: string | null;
}

const ROLE_TONES: Record<string, 'purple' | 'blue' | 'amber' | 'gray'> = {
  SUPER_ADMIN: 'purple',
  SUPPORT: 'blue',
  BILLING: 'amber',
  VIEWER: 'gray',
};

const ROLE_NOTES: Array<[string, string]> = [
  ['SUPER_ADMIN', 'Unrestricted. Can create tenants, providers and other platform users.'],
  ['SUPPORT', 'Read everything, plus bots, assignments and Vicidial users. No billing changes.'],
  ['BILLING', 'Read everything, plus plans, invoices and pricing rules.'],
  ['VIEWER', 'Read-only across the platform.'],
];

export default function PlatformUsers() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('SUPPORT');
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['admin-platform-users'],
    queryFn: () => api.get<{ data: PlatformUser[] }>('/admin/platform-users'),
  });

  const create = useMutation({
    mutationFn: () => api.post('/admin/platform-users', { email, name, password, role }),
    onSuccess: () => {
      setOpen(false);
      setEmail('');
      setName('');
      setPassword('');
      queryClient.invalidateQueries({ queryKey: ['admin-platform-users'] });
      setToast({ message: 'Platform user created', tone: 'good' });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Could not create user'),
        tone: 'bad',
      }),
  });

  if (isPending) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Platform users"
        description="People who administer the platform itself, across every tenant."
        actions={<Button onClick={() => setOpen(true)}>New platform user</Button>}
      />

      <Card padded={false}>
        <ul className="divide-y divide-line">
          {data.data.map((user) => (
            <li key={user.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-ink-900 dark:text-ink-50">{user.name}</div>
                <div className="text-xs text-ink-500">{user.email}</div>
              </div>
              <Badge tone={ROLE_TONES[user.role] ?? 'gray'}>{user.role.replace(/_/g, ' ')}</Badge>
              <Badge tone={user.status === 'ACTIVE' ? 'green' : 'gray'}>{user.status}</Badge>
              <span className="w-32 text-right text-xs text-ink-500">
                {formatRelative(user.last_login_at)}
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Roles" className="mt-4">
        <dl className="space-y-2">
          {ROLE_NOTES.map(([roleName, note]) => (
            <div key={roleName} className="flex flex-wrap gap-x-3 text-sm">
              <dt className="w-32 shrink-0 font-mono text-xs text-ink-500">{roleName}</dt>
              <dd className="flex-1 text-ink-600 dark:text-ink-300">{note}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="New platform user"
        description="Platform users are not attached to any tenant."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!email || !name || password.length < 12}
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Create
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Email" required>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="Full name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Role" required>
            <Select value={role} onChange={(e) => setRole(e.target.value)}>
              {Object.keys(ROLE_TONES).map((value) => (
                <option key={value} value={value}>
                  {value.replace(/_/g, ' ')}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Password"
            required
            hint="At least 12 characters."
            error={password && password.length < 12 ? 'Too short' : undefined}
          >
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
        </div>
      </Modal>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
